import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useIniciarManutencao } from '../hooks';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import type { MaintenanceType } from '../api';

const MAINTENANCE_TYPES: Array<{ value: MaintenanceType; label: string }> = [
  { value: 'preventive', label: 'Preventiva' },
  { value: 'corrective', label: 'Corretiva' },
  { value: 'component_replacement', label: 'Troca de componentes' },
  { value: 'furniture_refurbishment', label: 'Reforma de mobiliário' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  assetId: string;
}

export function MaintenanceStartModal({ open, onClose, assetId }: Props) {
  const mutation = useIniciarManutencao(assetId);
  const [description, setDescription] = useState('');
  const [maintenanceType, setMaintenanceType] = useState<MaintenanceType>('corrective');
  const [provider, setProvider] = useState('');
  const [expectedReturnAt, setExpectedReturnAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDescription(''); setMaintenanceType('corrective'); setProvider(''); setExpectedReturnAt(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!description.trim()) { setError('Descreva o motivo da manutenção'); return; }
    try {
      await mutation.mutateAsync({
        maintenanceType,
        description: description.trim(),
        provider: provider.trim() || undefined,
        expectedReturnAt: expectedReturnAt ? new Date(expectedReturnAt).toISOString() : undefined,
      });
      reset(); onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao iniciar manutenção'));
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">Enviar para manutenção</Dialog.Title>
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <label className="block space-y-1 text-sm text-gray-700">
              <span>Tipo de manutenção *</span>
              <select
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm"
                value={maintenanceType}
                onChange={(event) => setMaintenanceType(event.target.value as MaintenanceType)}
              >
                {MAINTENANCE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </label>
            <Textarea
              label="Descrição do problema *"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <TextInput
              label="Assistência técnica (opcional)"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            />
            <TextInput
              label="Previsão de retorno (opcional)"
              type="date"
              value={expectedReturnAt}
              onChange={(e) => setExpectedReturnAt(e.target.value)}
            />

            {error && <Alert variant="error">{error}</Alert>}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
              <Button
                type="submit"
                loading={mutation.isPending}
                className="!bg-purple-600 hover:!bg-purple-700 focus:!ring-purple-500"
              >
                {mutation.isPending ? 'Enviando…' : 'Confirmar envio'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
